import { useEffect, useState } from 'react';
import { formatInTimezone, useTimezone } from '@kioskgaming/ui';
import { getShopGameWalletTransactions } from '../../../services/api';
import type { ShopGameWalletTransaction } from '../../../types';

const TYPE_LABELS: Record<ShopGameWalletTransaction['type'], string> = {
  game_wallet_deposit: 'Game wallet deposit',
  game_wallet_recall: 'Game wallet recall',
  game_wallet_sync: 'Game wallet sync',
};

interface Props {
  providerId: string;
}

export default function TransactionsTab({ providerId }: Props) {
  const { timezone } = useTimezone();
  const [transactions, setTransactions] = useState<ShopGameWalletTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    setLoading(true);
    setError(null);
    getShopGameWalletTransactions({ providerId, page, limit })
      .then((res) => {
        if (res.success && res.data) {
          setTransactions(res.data.items);
          setTotal(res.data.total);
        } else {
          setError(res.message || 'Failed to load transactions');
        }
      })
      .catch(() => setError('Failed to load transactions'))
      .finally(() => setLoading(false));
  }, [providerId, page]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
        <span className="font-semibold">Statistics rule:</span> Game wallet deposit and Game wallet recall are internal wallet movements. Do not count them in Total External Player Deposits or Total External Player Withdrawals.
      </div>

      {loading && <p className="py-8 text-center text-sm text-stone-400">Loading…</p>}
      {error && <p className="py-6 text-center text-sm text-red-600">{error}</p>}

      {!loading && !error && (
        <div className="overflow-x-auto rounded-lg border border-stone-200">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="border-b border-stone-100 bg-stone-50">
                {['Transaction ID', 'Player', 'Type', 'Amount', 'Status', 'Reference', 'Note', 'Created'].map((h) => (
                  <th key={h} className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {transactions.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-sm text-stone-400">
                    No transactions found
                  </td>
                </tr>
              )}
              {transactions.map((tx) => (
                <tr key={tx.id} className="hover:bg-stone-50">
                  <td className="px-3 py-3 font-mono text-xs text-stone-700">{tx.id}</td>
                  <td className="px-3 py-3 text-sm text-stone-900">{tx.playerName}</td>
                  <td className="px-3 py-3">
                    <span className="inline-flex rounded-full border border-stone-200 bg-stone-100 px-2 py-0.5 text-xs font-semibold text-stone-700">
                      {TYPE_LABELS[tx.type] ?? tx.type}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-sm tabular-nums text-stone-700">{Number(tx.amount).toFixed(2)}</td>
                  <td className="px-3 py-3">
                    <span
                      className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${
                        tx.status === 'completed' || tx.status === 'success'
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : tx.status === 'failed'
                          ? 'border-red-200 bg-red-50 text-red-700'
                          : 'border-amber-200 bg-amber-50 text-amber-700'
                      }`}
                    >
                      {tx.status}
                    </span>
                  </td>
                  <td className="px-3 py-3 font-mono text-xs text-stone-500">{tx.reference || '—'}</td>
                  <td className="px-3 py-3 text-xs text-stone-500">{tx.note || '—'}</td>
                  <td className="px-3 py-3 text-xs text-stone-500">
                    {tx.createdAt ? formatInTimezone(tx.createdAt, timezone) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-stone-600">
          <span>{total} transactions</span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded border border-stone-200 px-3 py-1 disabled:opacity-40 hover:bg-stone-50"
            >
              Previous
            </button>
            <span className="flex items-center px-2">{page} / {totalPages}</span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border border-stone-200 px-3 py-1 disabled:opacity-40 hover:bg-stone-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
